import { ModalsKey } from '@/enums/modalsKey';
import { useModalNavigation } from '@/hooks/modals/useModalNavigation';
import { useSearchParams } from '@/hooks/useSearchParams';
import { LuckySpinId } from '@/models/user-reward/lucky-spin/namespace';
import { Image } from '@starsoft/common/components';
import Color from 'color';
import { useCallback, useMemo } from 'react';

import useTranslation from 'next-translate/useTranslation';
import { LuckySpinHeaderButtonOptionsProps } from './props';
import styles from './styles.module.scss';

import { useUserRewardsMe } from '@/api/user-rewards';
import { RewardId } from '@/enums/reward-id';
import { RootState } from '@/lib/store';
import { parseToBigInt } from '@starsoft/common/utils/parsers';
import { useSelector } from 'react-redux';
import skeleton from './skeleton.module.scss';

export default function LuckySpinHeaderOptions({
  isLoading,
}: LuckySpinHeaderButtonOptionsProps) {
  const search = useSearchParams();
  const { t, lang: language } = useTranslation('common');
  const mode = (search.get('mode') as string) ?? 1;
  const { rewards } = useUserRewardsMe({ rewardId: RewardId.LuckyWheel });
  const { navigateToModal } = useModalNavigation();

  const isSpinning: boolean = useSelector(
    (state: RootState) => state.luckSpin.isSpinning,
  );

  const handleChangeMode = useCallback(
    (mode: number) => {
      if (isSpinning) {
        return;
      }
      const newUrlSearchParams = new URLSearchParams(search);
      newUrlSearchParams.set('mode', String(mode));

      navigateToModal(ModalsKey.LuckySpin, `?${newUrlSearchParams.toString()}`);
    },
    [search, navigateToModal, isSpinning],
  );

  const Options = useMemo(
    () =>
      rewards?.map(opt => {
        const rankName = LuckySpinId.idToGameName(opt?.id);
        const isActive = parseToBigInt(opt.id) == parseToBigInt(mode);
        const image = `${
          process.env.NEXT_PUBLIC_S3_BUCKET_BASE_URL
        }/artworks/lucky-spin/${rankName}/png/${
          isActive
            ? `ice-spin-icon-${rankName.toLowerCase()}.png`
            : `ice-spin-icon-inactive.png`
        }`;

        return (
          <div
            key={`lucky_spin_option_button_${opt?.id}`}
            onClick={() => handleChangeMode(Number(opt.id))}
            className={`${styles.button} ${isActive ? styles['button--active'] : ''}`}
            style={{
              backgroundColor: isActive
                ? `${Color(opt?.spin?.rankGroup?.color).alpha(0.2).toString()}`
                : undefined,
            }}
          >
            <Image
              format="square"
              alt="lucky-spin-button-img"
              width={40}
              height={40}
              preload
              className={`${styles.button__image}`}
              src={image}
              secondarySkeleton
            />
            {isActive && (
              <div
                className={`${styles.button__info} ${isActive ? styles['button__info--active'] : ''}`}
              >
                <div className={styles.button__info__title}>
                  {opt?.spin?.rankGroup?.name?.[language]}
                </div>
                <p className={styles.button__info__description}>
                  {t('current_spin_info', {
                    level: opt?.spin?.rankGroup?.firstRank?.name?.[language],
                  })}
                </p>
              </div>
            )}
          </div>
        );
      }),
    [rewards, mode, handleChangeMode, t, language],
  );

  const Skeletons = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, idx) => (
        <div
          className={`${styles.button} ${idx == 0 ? styles['button--active'] : ''}`}
          key={`lucky_spin_option_button_${idx}`}
        >
          <Image
            format="square"
            alt="lucky-spin-button-img"
            width={50}
            height={50}
            preload
            className={`${styles.button__image}`}
            secondarySkeleton
          />
          {idx == 0 && (
            <div className={styles.button__info}>
              <div className={skeleton.title} />
              <div className={skeleton.description} />
            </div>
          )}
        </div>
      )),
    [],
  );

  if (isLoading) {
    return Skeletons;
  }

  return Options;
}
